import {
  isDefined,
  toResponseError,
  type InstallId,
  type InstallSecretAddInput,
  type InstallSecretAddSuccess,
  type InstallSecretListInput,
  type InstallSecretListSuccess,
  type InstallSecretRemoveInput,
  type InstallSecretRemoveSuccess,
  type InstallSecretSummary,
  type InstallSecretUpdateInput,
  type InstallSecretUpdateSuccess,
  type InstallSecretValue,
} from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { useTantalimQuery } from '../query'
import { useTantalimMutation } from '../mutation'
import {
  mutationAdd,
  mutationRemove,
  mutationUpdate,
  type AddHookResponse,
  type GetHookResponse,
  type RemoveHookResponse,
  type UpdateHookResponse,
} from '../hooks'
import { connectInstallSecretKey } from './keys'

export function useConnectInstallSecrets(
  installId?: InstallId,
  enabled: boolean = true
): GetHookResponse<InstallSecretSummary[]> {
  const input: InstallSecretListInput | undefined = isDefined(installId) ? { installId } : undefined

  return useTantalimQuery<InstallSecretListInput | undefined, InstallSecretListSuccess, InstallSecretSummary[]>({
    url: '/connect/install/secret/list',
    queryKey: connectInstallSecretKey(installId),
    input,
    convertResponse: ({ secrets }) => secrets,
    options: {
      enabled: isDefined(installId) && enabled,
    },
  })
}

export function useAddConnectInstallSecret(): AddHookResponse<InstallSecretAddInput, InstallSecretSummary> {
  const mutation = useTantalimMutation<InstallSecretAddInput, InstallSecretAddSuccess>({
    url: '/connect/install/secret/add',
  })
  const queryClient = useQueryClient()

  async function add(input: InstallSecretAddInput): Promise<InstallSecretSummary> {
    try {
      const added = await mutation.mutateAsync(input)
      await queryClient.invalidateQueries({ queryKey: connectInstallSecretKey(input.secret.installId) })
      return added
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return mutationAdd(mutation, add)
}

export function useUpdateConnectInstallSecret(): UpdateHookResponse<InstallSecretUpdateInput, InstallSecretSummary> {
  const mutation = useTantalimMutation<InstallSecretUpdateInput, InstallSecretUpdateSuccess>({
    url: '/connect/install/secret/update',
  })
  const queryClient = useQueryClient()

  async function update(input: InstallSecretUpdateInput): Promise<InstallSecretSummary> {
    try {
      const updated = await mutation.mutateAsync(input)
      await queryClient.invalidateQueries({ queryKey: connectInstallSecretKey(input.secret.installId) })
      return updated
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return mutationUpdate(mutation, update)
}

export function useRemoveConnectInstallSecret(): RemoveHookResponse<InstallSecretRemoveInput> {
  const mutation = useTantalimMutation<InstallSecretRemoveInput, InstallSecretRemoveSuccess>({
    url: '/connect/install/secret/remove',
  })
  const queryClient = useQueryClient()

  async function remove(input: InstallSecretRemoveInput): Promise<void> {
    try {
      await mutation.mutateAsync(input)
      await queryClient.invalidateQueries({ queryKey: connectInstallSecretKey(input.secretId.installId) })
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return mutationRemove(mutation, remove)
}

export type InstallSecretMutation =
  | { type: 'add' | 'update'; name: string; value: InstallSecretValue }
  | { type: 'remove'; name: string; field?: string }

export type SaveConnectInstallSecretsResponse = {
  error?: string
  isSaving: boolean
  save: (mutations: InstallSecretMutation[], installId: InstallId) => Promise<void>
}

export function useSaveConnectInstallSecrets(): SaveConnectInstallSecretsResponse {
  const addSecret = useAddConnectInstallSecret()
  const updateSecret = useUpdateConnectInstallSecret()
  const removeSecret = useRemoveConnectInstallSecret()

  async function save(mutations: InstallSecretMutation[], installId: InstallId): Promise<void> {
    await Promise.all(
      mutations.map(async (mutation) => {
        if (mutation.type === 'remove') {
          await removeSecret.remove({ secretId: { installId, name: mutation.name, field: mutation.field } })
          return
        }
        const secret = { installId, name: mutation.name, value: mutation.value }
        if (mutation.type === 'add') {
          await addSecret.add({ secret })
          return
        }
        await updateSecret.update({ secret })
      })
    )
  }

  return {
    error: addSecret.error ?? updateSecret.error ?? removeSecret.error,
    isSaving: addSecret.isAdding || updateSecret.isUpdating || removeSecret.isRemoving,
    save,
  }
}

export type ConnectInstallSecretMutationState = {
  error?: string
  isAdding: boolean
  isRemoving: boolean
  isUpdating: boolean
}

export function getConnectInstallSecretMutationState({
  addSecret,
  removeSecret,
  updateSecret,
}: {
  addSecret: Pick<AddHookResponse<InstallSecretAddInput, InstallSecretSummary>, 'error' | 'isAdding'>
  removeSecret: Pick<RemoveHookResponse<InstallSecretRemoveInput>, 'error' | 'isRemoving'>
  updateSecret: Pick<UpdateHookResponse<InstallSecretUpdateInput, InstallSecretSummary>, 'error' | 'isUpdating'>
}): ConnectInstallSecretMutationState {
  return {
    error: addSecret.error ?? updateSecret.error ?? removeSecret.error,
    isAdding: addSecret.isAdding,
    isRemoving: removeSecret.isRemoving,
    isUpdating: updateSecret.isUpdating,
  }
}
